import React, { useMemo, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RevealOnScroll } from "../shared";
import { SPEC_THESIS, SPEC_LAYERS, SPECIFICATIONS } from "./specificationsContent";

const specById = Object.fromEntries(SPECIFICATIONS.map((s) => [s.id, s]));

const LayerNavItem = ({ layer, isActive, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(layer.id)}
    className={`w-full text-left pl-3 pr-2 py-2.5 rounded-r-lg transition-colors border-l-2 ${
      isActive
        ? "border-[#00D2FF] bg-[#00D2FF]/8"
        : "border-transparent hover:bg-[#11151F] hover:border-[#00D2FF]/25"
    }`}
    data-testid={`spec-layer-${layer.id}`}
  >
    <p className={`text-sm font-semibold ${isActive ? "text-partner" : "text-partner-muted"}`}>
      {layer.name}
    </p>
    <p className="text-xs text-partner-muted font-body mt-0.5">{layer.tagline}</p>
  </button>
);

const LayerPill = ({ layer, isActive, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(layer.id)}
    className={`flex-shrink-0 rounded-full border px-3.5 py-2 text-xs font-semibold whitespace-nowrap transition-colors ${
      isActive
        ? "border-[#00D2FF]/50 text-partner-accent bg-[#00D2FF]/15"
        : "border-partner text-partner-muted bg-[#080A0F]"
    }`}
    data-testid={`spec-layer-mobile-${layer.id}`}
  >
    {layer.name}
  </button>
);

const ModuleNavItem = ({ spec, isActive, onSelect, compact = false }) => (
  <button
    type="button"
    onClick={() => onSelect(spec.id)}
    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
      isActive
        ? "text-partner-accent font-semibold bg-[#00D2FF]/8"
        : "text-partner-muted hover:text-partner hover:bg-[#11151F]"
    }`}
    data-testid={`spec-module-${spec.id}`}
  >
    {compact ? (
      <>
        <p className="text-[10px] font-mono text-partner-accent/80 leading-snug">{spec.subtitle}</p>
        <p className="text-sm leading-snug mt-0.5 line-clamp-2">{spec.title}</p>
      </>
    ) : (
      <span className="text-sm">{spec.title}</span>
    )}
  </button>
);

const ModuleDetail = ({ spec }) => (
  <motion.div
    key={spec.id}
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -4 }}
    transition={{ duration: 0.2 }}
    data-testid={`spec-detail-${spec.id}`}
  >
    <p className="text-[10px] font-mono text-partner-accent/80 mb-2 lg:hidden">{spec.subtitle}</p>
    <h4 className="text-lg sm:text-xl font-bold text-partner mb-3">{spec.title}</h4>
    <p className="text-sm text-partner-muted font-body leading-relaxed">{spec.description}</p>
  </motion.div>
);

const DetailPanel = ({ selectedSpec, panelRef }) => (
  <div
    ref={panelRef}
    className="rounded-xl border border-[#00D2FF]/20 bg-[#11151F] p-5 sm:p-6 min-h-[12rem]"
  >
    <AnimatePresence mode="wait">
      {selectedSpec && <ModuleDetail spec={selectedSpec} />}
    </AnimatePresence>
  </div>
);

const SpecificationsSection = () => {
  const [activeLayerId, setActiveLayerId] = useState(SPEC_LAYERS[0].id);
  const [selectedSpecId, setSelectedSpecId] = useState(SPEC_LAYERS[0].moduleIds[0]);
  const mobileDetailRef = useRef(null);

  const activeLayer = SPEC_LAYERS.find((l) => l.id === activeLayerId) ?? SPEC_LAYERS[0];

  const layerModules = useMemo(
    () => activeLayer.moduleIds.map((id) => specById[id]).filter(Boolean),
    [activeLayer]
  );

  const selectedSpec = specById[selectedSpecId] ?? layerModules[0];
  const selectedIndex = layerModules.findIndex((s) => s.id === selectedSpec?.id);

  const handleLayerSelect = (layerId) => {
    const layer = SPEC_LAYERS.find((l) => l.id === layerId);
    if (!layer) return;
    setActiveLayerId(layerId);
    setSelectedSpecId(layer.moduleIds[0]);
  };

  const handleModuleSelect = useCallback((specId) => {
    setSelectedSpecId(specId);
    requestAnimationFrame(() => {
      mobileDetailRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, []);

  return (
    <section
      className="section-padding section-alt relative overflow-hidden"
      data-testid="specifications-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <RevealOnScroll>
          <h2 className="text-3xl sm:text-4xl font-bold text-partner leading-tight mb-3 max-w-3xl">
            {SPEC_THESIS.headline.split("Specifications")[0]}
            <span className="gradient-text">Specifications</span>
          </h2>
          <p className="text-lg font-semibold text-partner leading-snug max-w-3xl mb-2">
            {SPEC_THESIS.insight}
          </p>
          <p className="text-sm text-partner-muted font-body max-w-2xl mb-8">{SPEC_THESIS.subhead}</p>
        </RevealOnScroll>

        <RevealOnScroll delay={0.1}>
          {/* Mobile: layer pills → detail → module list */}
          <div className="lg:hidden" data-testid="spec-blueprint-mobile">
            <div
              className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1"
              role="tablist"
              aria-label="Specification layers"
            >
              {SPEC_LAYERS.map((layer) => (
                <LayerPill
                  key={layer.id}
                  layer={layer}
                  isActive={activeLayerId === layer.id}
                  onSelect={handleLayerSelect}
                />
              ))}
            </div>

            <p className="text-xs text-partner-muted font-body mb-4">
              {activeLayer.tagline} · {layerModules.length} capabilities
            </p>

            <DetailPanel selectedSpec={selectedSpec} panelRef={mobileDetailRef} />

            <div className="mt-4">
              <p className="text-xs font-semibold text-partner-muted uppercase tracking-wider mb-2 px-1">
                {activeLayer.name} ·{" "}
                {selectedIndex >= 0 ? `${selectedIndex + 1} of ${layerModules.length}` : layerModules.length}
              </p>
              <div className="space-y-0.5">
                {layerModules.map((spec) => (
                  <ModuleNavItem
                    key={spec.id}
                    spec={spec}
                    isActive={selectedSpecId === spec.id}
                    onSelect={handleModuleSelect}
                    compact
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Desktop: 3-column master-detail */}
          <div
            className="hidden lg:grid lg:grid-cols-[minmax(0,11rem)_minmax(0,1fr)_minmax(0,1.2fr)] gap-6 lg:gap-8 items-start"
            data-testid="spec-blueprint"
          >
            <nav className="space-y-0.5" aria-label="Specification layers">
              {SPEC_LAYERS.map((layer) => (
                <LayerNavItem
                  key={layer.id}
                  layer={layer}
                  isActive={activeLayerId === layer.id}
                  onSelect={handleLayerSelect}
                />
              ))}
            </nav>

            <div className="space-y-0.5 border-l border-partner pl-6">
              <p className="text-xs font-semibold text-partner-muted mb-3 px-3">
                {activeLayer.name} · {layerModules.length} capabilities
              </p>
              {layerModules.map((spec) => (
                <ModuleNavItem
                  key={spec.id}
                  spec={spec}
                  isActive={selectedSpecId === spec.id}
                  onSelect={setSelectedSpecId}
                />
              ))}
            </div>

            <DetailPanel selectedSpec={selectedSpec} panelRef={null} />
          </div>
        </RevealOnScroll>
      </div>
    </section>
  );
};

export default SpecificationsSection;
